import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, ShieldCheck, Power, PowerOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "../lib/api";

const ADMIN_EMAILS = new Set([
  "rmjcoordinator@gmail.com",
  "admin@ramajeyamrice.com",
  "rotnsormj@gmail.com",
]);

export default function UserManagement() {
  const [mappings, setMappings] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("salesperson");
  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setMappings(data.mappings || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      await api.post("/users", { email: e, role });
      toast.success(`${e} set as ${role}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add user");
    }
  };

  const handleDelete = async (em) => {
    if (!window.confirm(`Remove access for ${em}?`)) return;
    setBusyEmail(em);
    try {
      await api.delete(`/users/${encodeURIComponent(em)}`);
      toast.success(`${em} removed`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    } finally {
      setBusyEmail("");
    }
  };

  const handleToggleActive = async (em, nextActive) => {
    setBusyEmail(em);
    try {
      await api.patch(`/users/${encodeURIComponent(em)}/active`, {
        is_active: nextActive,
      });
      toast.success(`${em} ${nextActive ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusyEmail("");
    }
  };

  return (
    <div className="grain ui-sans pb-16" data-testid="users-page">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 mt-8 space-y-6">
        <div>
          <h1 className="brand-serif text-3xl sm:text-4xl font-bold text-[#1d3a14]">
            User &amp; Role Management
          </h1>
          <p className="text-sm text-[#5b6b4a] mt-1">
            Map Google email addresses to roles. Only listed &amp; active emails
            can sign in.
          </p>
        </div>

        <div className="bg-white/85 border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-[#1d3a14] mb-3">Add / Update Mapping</h2>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                Google Email
              </Label>
              <Input
                data-testid="new-user-email-input"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="name@gmail.com"
                className="mt-1"
              />
            </div>
            <div className="min-w-[180px]">
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                Role
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="new-user-role-select" className="mt-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salesperson" data-testid="role-opt-salesperson">Salesperson</SelectItem>
                  <SelectItem value="coordinator" data-testid="role-opt-coordinator">Coordinator</SelectItem>
                  <SelectItem value="billing" data-testid="role-opt-billing">Billing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAdd}
              data-testid="add-user-button"
              className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </div>

        <div className="bg-white/85 border border-[#d6cdb4] rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="mappings-table">
            <thead className="bg-[#1d3a14] text-[#faf6ea]">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-[#5b6b4a]">
                    Loading…
                  </td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-[#5b6b4a]">
                    No mappings yet.
                  </td>
                </tr>
              ) : (
                mappings.map((m, i) => {
                  const isAdmin = ADMIN_EMAILS.has(m.email);
                  const isActive = m.is_active !== false;
                  const isBusy = busyEmail === m.email;
                  return (
                    <tr
                      key={m.email}
                      className={`border-t border-[#efe7cf] ${i % 2 === 0 ? "bg-[#fbf8ec]" : "bg-white"}`}
                      data-testid={`mapping-row-${m.email}`}
                    >
                      <td className="px-3 py-2 font-mono">
                        {m.email}
                        {isAdmin && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#1d3a14] text-[#faf6ea] px-2 py-0.5 rounded-full"
                            data-testid={`admin-badge-${m.email}`}
                          >
                            <ShieldCheck className="h-3 w-3" /> Admin
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 uppercase tracking-wider text-xs">
                        {m.role}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            "inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full " +
                            (isActive
                              ? "bg-[#1d3a14]/15 text-[#1d3a14] border border-[#1d3a14]/30"
                              : "bg-[#a04040]/10 text-[#a04040] border border-[#a04040]/30")
                          }
                          data-testid={`status-badge-${m.email}`}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-2">
                          {!isAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isBusy}
                              onClick={() => handleToggleActive(m.email, !isActive)}
                              data-testid={`toggle-active-${m.email}`}
                              className={
                                isActive
                                  ? "border-[#c4b991] text-[#5b6b4a] hover:bg-[#efe7cf]"
                                  : "border-[#1d3a14] text-[#1d3a14] hover:bg-[#1d3a14]/10"
                              }
                            >
                              {isActive ? (
                                <>
                                  <PowerOff className="h-3.5 w-3.5 mr-1" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Power className="h-3.5 w-3.5 mr-1" />
                                  Activate
                                </>
                              )}
                            </Button>
                          )}
                          {!isAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isBusy}
                              onClick={() => handleDelete(m.email)}
                              data-testid={`delete-user-${m.email}`}
                              className="border-red-300 text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              Remove
                            </Button>
                          )}
                          {isAdmin && (
                            <span className="text-xs text-[#5b6b4a] italic">
                              Locked (admin)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
